import { GearIcon, LightningIcon, PlusIcon, UploadSimpleIcon } from '@phosphor-icons/react'
import { ColumnDef, ColumnHelper, createColumnHelper, Row } from '@tanstack/react-table'
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { Routes } from '../../routes'
import {
  useGetLatestAnalysisForRepository,
  useGetRepositoryIntegrations,
  useGetWorkflows,
} from '../../utils/api-client/api-hooks'
import type { Repository, Tool } from '../../utils/api-client/api-schemas'
import { convertIntegrationTypeToTool } from '../../utils/repository-utils.tsx'
import type { DrawerContent } from '../drawers/workflow-drawer'

import classNames from 'classnames'
import { LatestAnalysisLink } from '../latest-analysis-link'
import { SyncLatestDropdown } from '../menus/sync-latest-dropdown'
import { SCMIcon } from '../third-party-branding/scm-icon'
import { TooltipWithHover } from '../tooltip'
import * as styles from './repositories-table.css'

export type UseRepositoriesTableColumnDefinitionsOptions = Omit<
  Parameters<typeof createRepositoryColumnDefinition>[0] &
    Parameters<typeof createFixesColumnDefinition>[0] &
    Parameters<typeof createAnalysisColumnDefinition>[0] &
    Parameters<typeof createActionsColumnDefinition>[0],
  'columnHelper'
>

export const useRepositoriesTableColumnDefinitions = ({
  setSelectedWorkflowRepositoryId,
  setSelectedSettingsRepositoryId,
  setWorkflowDrawerContent,
  onAddFindings,
  onSyncTool,
}: UseRepositoriesTableColumnDefinitionsOptions) => {
  return useMemo(() => {
    const columnHelper = createColumnHelper<Repository>()

    return [
      createRepositoryColumnDefinition({
        columnHelper,
        setSelectedWorkflowRepositoryId,
        setWorkflowDrawerContent,
      }),
      createFixesColumnDefinition({ columnHelper }),
      createAnalysisColumnDefinition({ columnHelper, onAddFindings }),
      createActionsColumnDefinition({
        columnHelper,
        setSelectedWorkflowRepositoryId,
        setSelectedSettingsRepositoryId,
        setWorkflowDrawerContent,
        onAddFindings,
        onSyncTool,
      }),
    ] satisfies ColumnDef<Repository>[]
  }, [
    setSelectedWorkflowRepositoryId,
    setSelectedSettingsRepositoryId,
    setWorkflowDrawerContent,
    onAddFindings,
    onSyncTool,
  ])
}

const RepositoryInfo: React.FC<{ repository: Pick<Repository, 'type' | 'full_name'> }> = ({ repository }) => {
  return (
    <span className={styles.repositoryName}>
      <div style={{ marginRight: '12px' }}>
        <SCMIcon type={repository.type} size="medium" />
      </div>
      {repository.full_name}
    </span>
  )
}

// #region Repository column

const createRepositoryColumnDefinition = ({
  columnHelper,
  setSelectedWorkflowRepositoryId,
  setWorkflowDrawerContent,
}: {
  columnHelper: ColumnHelper<Repository>
  setSelectedWorkflowRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  setWorkflowDrawerContent?: React.Dispatch<React.SetStateAction<DrawerContent>>
}) => {
  return columnHelper.accessor(row => row.full_name, {
    id: 'repository',
    cell: ({ row }) => (
      <RepositoryColumnCell
        row={row}
        setSelectedWorkflowRepositoryId={setSelectedWorkflowRepositoryId}
        setWorkflowDrawerContent={setWorkflowDrawerContent}
      />
    ),
    header: RepositoryColumnHeader,
  }) as ColumnDef<Repository>
}

const RepositoryColumnHeader: React.FC = () => <span>REPOSITORY</span>

const RepositoryColumnCell: React.FC<{
  row: Row<Repository>
  setSelectedWorkflowRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  setWorkflowDrawerContent?: React.Dispatch<React.SetStateAction<DrawerContent>>
}> = ({ row, setSelectedWorkflowRepositoryId, setWorkflowDrawerContent }) => {
  const { data: workflowsData } = useGetWorkflows({ repositoryId: row.original.id })
  const workflowCount = workflowsData?.total ?? 0

  const handleLightningClick = () => {
    setSelectedWorkflowRepositoryId(row.original.id)
    if (setWorkflowDrawerContent) {
      setWorkflowDrawerContent('manage')
    }
  }

  return (
    <div className={styles.repositoryNameContainer}>
      <Link
        to={Routes.RepositoryDetailsPage.createPath({ repositoryId: row.original.id })}
        className={styles.repositoryNameLink}
      >
        <RepositoryInfo repository={row.original} />
      </Link>
      {workflowCount > 0 && (
        <TooltipWithHover
          trigger={
            <button className={styles.workflowButton} aria-label="Workflows" onClick={handleLightningClick}>
              <LightningIcon size={16} weight="fill" className={styles.workflowIndicator} />
            </button>
          }
          size="small"
        >
          <span>
            Manage {workflowCount} {workflowCount === 1 ? 'workflow' : 'workflows'}
          </span>
        </TooltipWithHover>
      )}
    </div>
  )
}
// #endregion Repository column

// #region Fixes column
const createFixesColumnDefinition = ({ columnHelper }: { columnHelper: ColumnHelper<Repository> }) =>
  columnHelper.display({
    id: 'fixes',
    cell: () => <></>,
    header: () => <></>,
  })
// #endregion Fixes column

// #region Analysis column

type CreateAnalysisColumnDefinitionOptions = {
  columnHelper: ColumnHelper<Repository>
  onAddFindings: (repositoryId: string) => unknown
}

const createAnalysisColumnDefinition = ({ columnHelper, onAddFindings }: CreateAnalysisColumnDefinitionOptions) =>
  columnHelper.display({
    id: 'analysis',
    cell: AnalysisColumnCell({ onAddFindings }),
    header: AnalysisColumnHeader,
  })

const AnalysisColumnHeader: React.FC = () => <span className={styles.center}>LATEST SCAN WITH ANALYSIS</span>

const AnalysisColumnCell: ({
  onAddFindings,
}: Pick<CreateAnalysisColumnDefinitionOptions, 'onAddFindings'>) => React.FC<{
  row: Row<Repository>
}> =
  ({ onAddFindings }) =>
  ({ row }) => {
    const { data: latestAnalysis } = useGetLatestAnalysisForRepository({ repositoryId: row.original.id })

    return (
      <div className={styles.center}>
        {latestAnalysis ? (
          <LatestAnalysisLink latestAnalysis={latestAnalysis} />
        ) : (
          <a
            href="#"
            className={styles.uploadScanLink}
            onClick={e => {
              e.preventDefault()
              onAddFindings(row.original.id)
            }}
          >
            Add findings
            <UploadSimpleIcon size={15} className={styles.uploadIconLatestAnalysis} />
          </a>
        )}
      </div>
    )
  }

// #endregion Analysis column

// #region Actions column

type CreateActionsColumnDefinitionOptions = ActionsColumnCellFactoryOptions & {
  columnHelper: ColumnHelper<Repository>
}

const createActionsColumnDefinition = ({
  columnHelper,
  setSelectedWorkflowRepositoryId,
  setSelectedSettingsRepositoryId,
  setWorkflowDrawerContent,
  onAddFindings,
  onSyncTool,
}: CreateActionsColumnDefinitionOptions) =>
  columnHelper.display({
    id: 'actions',
    cell: ActionsColumnCell({
      setSelectedWorkflowRepositoryId,
      setSelectedSettingsRepositoryId,
      setWorkflowDrawerContent,
      onAddFindings,
      onSyncTool,
    }),
    header: ActionsColumnHeader,
  })

const ActionsColumnHeader: React.FC = () => <span>Actions</span>

type ActionsColumnCellFactoryOptions = {
  setSelectedWorkflowRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  setSelectedSettingsRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  setWorkflowDrawerContent?: React.Dispatch<React.SetStateAction<DrawerContent>>
  onAddFindings: (repositoryId: string) => unknown
  onSyncTool: (repositoryId: string, tool: Tool, repositoryName: string) => unknown
}

const ActionsColumnCell: (options: ActionsColumnCellFactoryOptions) => React.FC<{ row: Row<Repository> }> =
  ({
    setSelectedWorkflowRepositoryId,
    setSelectedSettingsRepositoryId,
    setWorkflowDrawerContent,
    onAddFindings,
    onSyncTool,
  }) =>
  ({ row }) => {
    const [isSyncDropdownOpen, setIsSyncDropdownOpen] = useState(false)
    const { data: integrations } = useGetRepositoryIntegrations({ repositoryId: row.original.id })
    const mappedTools: Tool[] = useMemo(
      () => [
        ...new Set(
          (integrations?._embedded?.items ?? []).map(integration => convertIntegrationTypeToTool(integration.type))
        ),
      ],
      [integrations]
    )

    return (
      <div
        className={classNames(styles.center, styles.actionsContainer)}
        data-dropdown-open={isSyncDropdownOpen || undefined}
      >
        <AddFindingsActionButton onAddFindings={onAddFindings} row={row} />
        <SyncLatestDropdown
          repositoryId={row.original.id}
          mappedTools={mappedTools}
          onSyncTool={tool => onSyncTool(row.original.id, tool, row.original.name)}
          triggerClassName={styles.actionButton}
          onOpenChange={setIsSyncDropdownOpen}
        />
        <ManageWorkflowsActionButton
          setSelectedWorkflowRepositoryId={setSelectedWorkflowRepositoryId}
          setWorkflowDrawerContent={setWorkflowDrawerContent}
          row={row}
        />
        <RepositorySettingsActionButton setSelectedSettingsRepositoryId={setSelectedSettingsRepositoryId} row={row} />
      </div>
    )
  }

const AddFindingsActionButton: React.FC<{
  row: Row<Repository>
  onAddFindings: (repositoryId: string) => unknown
}> = ({ onAddFindings, row }) => {
  return (
    <TooltipWithHover
      trigger={
        <button
          className={styles.actionButton}
          onClick={() => onAddFindings(row.original.id)}
          aria-label="Add findings for analysis"
        >
          <PlusIcon size={15} weight="light" />
        </button>
      }
      size="small"
    >
      <span>Add findings for analysis</span>
    </TooltipWithHover>
  )
}

const ManageWorkflowsActionButton: React.FC<{
  setSelectedWorkflowRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  setWorkflowDrawerContent?: React.Dispatch<React.SetStateAction<DrawerContent>>
  row: Row<Repository>
}> = ({ setSelectedWorkflowRepositoryId, setWorkflowDrawerContent, row }) => {
  const handleClick = () => {
    setSelectedWorkflowRepositoryId(row.original.id)
    if (setWorkflowDrawerContent) {
      setWorkflowDrawerContent('create')
    }
  }
  return (
    <TooltipWithHover
      trigger={
        <button className={styles.actionButton} onClick={handleClick} aria-label="Create workflow">
          <LightningIcon size={15} weight="light" />
        </button>
      }
      size="small"
    >
      <span>Create workflow</span>
    </TooltipWithHover>
  )
}

const RepositorySettingsActionButton: React.FC<{
  setSelectedSettingsRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  row: Row<Repository>
}> = ({ setSelectedSettingsRepositoryId, row }) => {
  return (
    <TooltipWithHover
      trigger={
        <button
          className={styles.actionButton}
          onClick={() => setSelectedSettingsRepositoryId(row.original.id)}
          aria-label="Repository settings"
        >
          <GearIcon size={15} weight="light" />
        </button>
      }
      size="small"
    >
      <span>Repository settings</span>
    </TooltipWithHover>
  )
}

// #endregion Actions column
